import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, PenSquare, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '../components/Layout';
import { AdminGate, useAdminAccess } from '../components/AdminGate';
import { PageHeader } from '../components/PageHeader';
import { TabCount } from '../components/TabCount';
import { PageToolbar } from '../components/PageToolbar';
import { BackButton } from '../components/BackButton';
import { Conversation } from '../components/support/Conversation';
import { OnboardingTrack } from '../components/support/OnboardingTrack';
import { AccountAccessPanel } from '../components/support/AccountAccessPanel';
import { NewMessageDialog } from '../components/support/NewMessageDialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useRegion } from '../hooks/useRegion';
import { cn } from '../lib/utils';
import {
  formatMessageTime, ONBOARDING_STAGES, planName, statusClass, statusLabel, supportApi, toastApiError,
} from '../lib/support';

const VIEWS = [
  { value: 'needs_reply', label: 'Needs reply' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'closed', label: 'Closed' },
  { value: 'all', label: 'All' },
];

/**
 * Support: every customer conversation, for admins. The list on the left,
 * the open conversation in the middle, and on a wide screen the customer's
 * account beside it. Below the desktop breakpoint it is list, then
 * conversation, like any list page and its record.
 */
export default function SupportPage() {
  const adminAccess = useAdminAccess();
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { region } = useRegion();
  const [view, setView] = useState('needs_reply');
  const [search, setSearch] = useState('');
  const [threads, setThreads] = useState(null);
  const [counts, setCounts] = useState({});
  const [composing, setComposing] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const [list, counts] = await Promise.all([supportApi.threads(view, search), supportApi.counts()]);
      setThreads(list);
      setCounts(counts);
    } catch (e) {
      setThreads([]);
      toastApiError(e, 'Could not load conversations');
    }
  }, [view, search]);

  // Search waits for a pause in typing; the list refreshes itself every 30s
  useEffect(() => {
    const t = setTimeout(loadList, search ? 300 : 0);
    const refresh = setInterval(loadList, 30000);
    return () => { clearTimeout(t); clearInterval(refresh); };
  }, [loadList, search]);

  const access = adminAccess;
  if (access !== 'yes') return <AdminGate access={access} />;

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader title="Support" />
        <PageToolbar className={cn(threadId && 'hidden lg:flex')}>
          <div className="flex flex-wrap items-center gap-4 min-w-0">
            {/* A row that cannot fit scrolls sideways itself (DESIGN.md) */}
            <div className="overflow-x-auto max-w-full">
              <Tabs value={view} onValueChange={setView}>
                <TabsList>
                  {VIEWS.map((v) => (
                    <TabsTrigger key={v.value} value={v.value} data-testid={`support-view-${v.value}`}>
                      {v.label}
                      <TabCount count={counts[v.value]} />
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="relative sm:w-64">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search conversations"
                className="pl-10"
                data-testid="support-search"
              />
            </div>
            <Button onClick={() => setComposing(true)} data-testid="support-new-message">
              <PenSquare />
              New message
            </Button>
          </div>
        </PageToolbar>

        <div className="lg:grid lg:grid-cols-[minmax(260px,340px)_minmax(0,1fr)] min-[1280px]:grid-cols-[minmax(260px,320px)_minmax(0,1fr)_280px] gap-6 items-start">
          <div className={cn(threadId && 'hidden lg:block')}>
            <ThreadList threads={threads} activeId={threadId} region={region} onOpen={(id) => navigate(`/support/${id}`)} view={view} />
          </div>
          {threadId ? (
            <AdminThread key={threadId} threadId={threadId} region={region} onChanged={loadList} />
          ) : (
            <div className="hidden lg:block rounded-cards bg-surface p-8 text-muted-foreground">
              Choose a conversation.
            </div>
          )}
        </div>
      </div>
      <NewMessageDialog
        open={composing}
        onOpenChange={setComposing}
        onSent={(t) => { toast.success(`Sent to ${t.customer_name || t.customer_email}`); loadList(); navigate(`/support/${t.id}`); }}
      />
    </Layout>
  );
}

function ThreadList({ threads, activeId, region, onOpen, view }) {
  if (threads === null) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }
  if (threads.length === 0) {
    return (
      <div className="rounded-cards bg-surface p-6 text-muted-foreground" data-testid="support-empty">
        {view === 'needs_reply' ? 'Nobody is waiting on us.' : view === 'onboarding' ? 'No onboarding in progress.' : 'No conversations here.'}
      </div>
    );
  }
  return (
    <ul className="space-y-2" data-testid="support-threads">
      {threads.map((t) => (
        <li key={t.id}>
          <button
            type="button"
            onClick={() => onOpen(t.id)}
            className={cn(
              'w-full text-left rounded-cards p-4 transition-colors min-w-0',
              t.id === activeId ? 'bg-inverse text-inverse-foreground' : 'bg-surface hover:bg-mist'
            )}
          >
            <span className="flex items-baseline justify-between gap-3">
              <span className={cn('truncate', t.unread && 'font-semibold')}>{t.customer_name || t.customer_email}</span>
              <span className={cn('text-xs tabular-nums shrink-0', t.id === activeId ? 'text-inverse-muted' : 'text-muted-foreground')}>
                {formatMessageTime(t.last_message_at, region)}
              </span>
            </span>
            <span className={cn('block truncate text-sm mt-1', t.unread && 'font-medium')}>{t.subject}</span>
            <span className="flex items-center gap-2 mt-2">
              <span className={cn('text-xs px-2 py-0.5 rounded-pill', t.id === activeId ? 'bg-mist text-foreground' : statusClass(t.status))}>
                {statusLabel(t.status, 'admin')}
              </span>
              {t.kind === 'onboarding' && (
                <span className={cn('text-xs', t.id === activeId ? 'text-inverse-muted' : 'text-muted-foreground')}>
                  {ONBOARDING_STAGES.find((st) => st.value === t.onboarding_stage)?.label}
                </span>
              )}
              {t.kind !== 'onboarding' && (
                <span className={cn('text-xs', t.id === activeId ? 'text-inverse-muted' : 'text-muted-foreground')}>{t.kind_label}</span>
              )}
              {t.unread && <span className="w-2 h-2 rounded-full bg-brick ml-auto" aria-label="Unread" />}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function AdminThread({ threadId, region, onChanged }) {
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  // Opening a thread marks it read, so the list is refreshed after. Held in a
  // ref so a new search or tab doesn't reload the open conversation.
  const refreshList = useRef(onChanged);
  refreshList.current = onChanged;

  useEffect(() => {
    supportApi.thread(threadId).then((t) => { setThread(t); refreshList.current(); }).catch((e) => {
      toastApiError(e, 'Could not open that conversation');
      navigate('/support');
    });
  }, [threadId, navigate]);

  const send = async ({ message, internal, status, files }) => {
    try {
      setThread(files.length
        ? await supportApi.adminUpload(threadId, files, message, status)
        : await supportApi.adminReply(threadId, { message, internal, status }));
      onChanged();
      toast.success(internal ? 'Note added' : `Sent to ${thread.customer_name || thread.customer_email}`);
      return true;
    } catch (e) {
      toastApiError(e, 'Could not send');
      return false;
    }
  };

  const changeStage = async (stage) => {
    try {
      setThread(await supportApi.setStage(threadId, stage));
      onChanged();
      toast.success(`Onboarding moved to ${ONBOARDING_STAGES.find((st) => st.value === stage)?.label}`);
    } catch (e) {
      toastApiError(e, 'Could not change the stage');
    }
  };

  const changeStatus = async (status) => {
    try {
      setThread(await supportApi.setStatus(threadId, status));
      onChanged();
    } catch (e) {
      toastApiError(e, 'Could not change the status');
    }
  };

  if (!thread) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <>
      <div className="space-y-6 min-w-0">
        <div className="space-y-3">
          <div className="title-row gap-3 min-w-0">
            <BackButton onClick={() => navigate('/support')} label="Back to support" className="lg:hidden" />
            <h2 className="record-title min-w-0 break-words">{thread.subject}</h2>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Select value={thread.status} onValueChange={changeStatus}>
              <SelectTrigger className="w-auto min-w-44" data-testid="support-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['open', 'waiting_on_customer', 'closed'].map((s) => (
                  <SelectItem key={s} value={s}>{statusLabel(s, 'admin')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {thread.kind === 'onboarding' && (
              <Select value={thread.onboarding_stage} onValueChange={changeStage}>
                <SelectTrigger className="w-auto min-w-44" data-testid="support-stage" aria-label="Onboarding stage">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ONBOARDING_STAGES.map((st) => <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <span className="text-sm text-muted-foreground">
              {thread.kind_label}
              {thread.context?.page && <> &middot; from {thread.context.page}</>}
            </span>
          </div>
          {thread.kind === 'onboarding' && <OnboardingTrack stage={thread.onboarding_stage} side="admin" />}
        </div>
        <Conversation
          messages={thread.messages}
          mode="admin"
          customerName={thread.customer_name || thread.customer_email}
          region={region}
          onSend={send}
          getFileLink={supportApi.fileLink}
        />
        {/* Below 1280px the customer's card follows the conversation */}
        <div className="min-[1280px]:hidden"><CustomerCard thread={thread} region={region} /></div>
      </div>
      <div className="hidden min-[1280px]:block"><CustomerCard thread={thread} region={region} /></div>
    </>
  );
}

function CustomerCard({ thread, region }) {
  const navigate = useNavigate();
  const c = thread.customer;
  if (!c) return null;
  const rows = [
    ['Plan', `${planName(c.plan)}${c.status ? ` · ${c.status}` : ''}`],
    c.trial_ends_at && ['Trial ends', formatMessageTime(c.trial_ends_at, region)],
    c.cancel_at_period_end && ['Cancels', formatMessageTime(c.current_period_end, region)],
    ['Region', c.region || 'Not set'],
    ['Signed up', formatMessageTime(c.created_at, region) || '-'],
    ['Last sign-in', formatMessageTime(c.last_login_at, region) || '-'],
    ['Jobs', c.job_count],
    ['Equipment', c.equipment_count],
    c.onboarding_purchased && ['Onboarding', c.onboarding_completed ? 'Done' : 'Booked'],
  ].filter(Boolean);

  return (
    <aside className="rounded-cards bg-surface p-5 space-y-5 min-w-0" data-testid="support-customer-card">
      <div className="min-w-0">
        <p className="eyebrow">customer</p>
        <p className="text-lg mt-1 truncate">{c.name || c.email}</p>
        <a href={`mailto:${c.email}`} className="text-sm text-clay underline underline-offset-4 break-all">{c.email}</a>
      </div>
      <dl className="text-sm divide-y divide-line">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-4 py-2">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="text-right tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>
      {thread.kind === 'onboarding' && <AccountAccessPanel thread={thread} region={region} />}
      {thread.other_threads?.length > 0 && (
        <div className="space-y-2">
          <p className="eyebrow">other conversations</p>
          <ul className="space-y-1 text-sm">
            {thread.other_threads.map((o) => (
              <li key={o.id}>
                <button type="button" onClick={() => navigate(`/support/${o.id}`)} className="text-left text-clay underline underline-offset-4 hover:text-foreground">
                  {o.subject}
                </button>
                <span className="text-muted-foreground"> &middot; {statusLabel(o.status, 'admin')}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </aside>
  );
}
